import { Component, Inject, OnInit } from '@angular/core';
import { FormGroup, FormArray, Validators, FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment';
import { take } from 'rxjs';
import { RelatoriosContratuaisService } from 'src/app/modules/services/relatorios-contratuais/relatorios-contratuais.service';
import { RelatoriosOperacionaisService } from 'src/app/modules/services/relatorios-operacionais/relatorios-operacionais.service';
import { ToastService } from 'src/app/modules/services/toast/toast.service';

export const mockNomesRelatorios = [
  { id: 1, nome: 'Relatório de Riscos Operacionais' },
  { id: 2, nome: 'Relatório de Execução Contratual' },
  { id: 3, nome: 'Relatório de Acompanhamento de Obras' },
];

export const mockTiposRelatorio = [
  { id: 1, nome: 'Monitoração', descricao: 'Monitoração' },
  { id: 2, nome: 'Expansão', descricao: 'Expansão' },
  { id: 3, nome: 'Fiscalização', descricao: 'Fiscalização' },
];

export const mockAreasFuncionais = [
  { id: 1, nome: 'Planejamento Estratégico' },
  { id: 2, nome: 'Execução de Obras' },
  { id: 3, nome: 'Análise de Riscos' },
];

@Component({
  selector: 'app-form-relatorio',
  templateUrl: './form-relatorio.component.html',
  styleUrls: ['./form-relatorio.component.scss'],
})
export class FormRelatorioComponent implements OnInit {
  form!: FormGroup;
  frequencias!: FormArray;
  nomesRelatorios: any[] = [];
  tiposRelatorio: any[] = [];
  areasFuncionais: any[] = [];
  prazos: string[] = [
    'Mensal',
    'Bimestral',
    'Trimestral',
    'Quadrimestral',
    'Semestral',
    'Anual',
    'Bienal',
    'Quinquenal',
    'Personalizado',
  ];
  datasBase: string[] = ['Eficácia'];
  frequenciasDisponiveis: string[] = [
    'Mensal',
    'Bimestral',
    'Trimestral',
    'Quadrimestral',
    'Semestral',
    'Anual',
    'Bienal',
    'Quinquenal',
  ];

  idRelatorio: any;
  idContract: any;
  isContratualEditManual: boolean = false;
  isOperacionalEditManual: boolean = false;
  from: 'contratual' | 'operacional' = 'contratual';

  /** Evita aplicar respostas antigas quando o usuário troca o tipo rapidamente. */
  private operacionalCargaSeq = 0;

  constructor(
    private fb: FormBuilder,
    private relatoriosContratuaisService: RelatoriosContratuaisService,
    private relatoriosOperacionaisService: RelatoriosOperacionaisService,
    public toastService: ToastService,
    private dialogRef: MatDialogRef<FormRelatorioComponent>,
    @Inject(MAT_DIALOG_DATA) private data: any,
  ) {
    this.idContract = this.data.idContract;
    this.idRelatorio = this.data.idRelatorio;
    if (this.data.from) {
      this.from = this.data.from;
    }
  }

  ngOnInit(): void {
    // Inicialização do formulário com base na origem
    this.initializeForm();
    // Inicialização do FormArray de frequências
    this.frequencias = this.form.get('frequencias') as FormArray;
    // Lógica de carregamento de dados com base na origem
    this.loadData();
    // Chamadas de serviço comuns
    if (this.from == 'contratual') {
      this.getAreasFuncionais();
      this.observarTipoRelatorio();
    }

    // Carrega dados do relatório se houver
    if (this.data.idRelatorio) {
      this.carregarRelatorio(this.data.idRelatorio);
    }
  }

  private initializeForm(): void {
    if (this.from === 'contratual') {
      this.prazos.unshift('Manual');
      this.handleContratualFormCreation();
    } else if (this.from === 'operacional') {
      this.prazos.unshift('Manual');
      this.handleOperacionalFormCreation();
    }
  }

  private handleContratualFormCreation(): void {
    // Campos comuns para relatórios contratuais
    const commonFields = {
      idRelatorio: [this.idRelatorio, Validators.required],
      idTipoRelatorio: [null, Validators.required],
      idAreaFuncional: [{ value: null, disabled: false }],
      itemContratual: [null, Validators.required],
      observacao: [null],
    };
    if (!this.idRelatorio) {
      // Caso de novo relatório contratual
      this.form = this.fb.group({
        ...commonFields,
        tipoPrazo: [null, Validators.required],
        tipoDataBase: [{ value: null, disabled: true }],
        periodoReferenciaInicio: [null],
        periodoReferenciaFim: [null],
        prazo: [null],
        frequencias: this.fb.array([]),
      });
    } else {
      // Caso de edição de relatório contratual
      this.form = this.fb.group({
        ...commonFields,
        periodoReferenciaInicio: [null],
        periodoReferenciaFim: [null],
        prazo: [null, Validators.required],
      });
    }
  }

  private handleOperacionalFormCreation(): void {
    // Campos comuns para relatórios operacionais
    const commonFields = {
      idRelatorio: [
        { value: this.idRelatorio, disabled: true },
        Validators.required,
      ],
      idTipoRelatorio: [null, Validators.required],
      /** Só habilita após escolher o tipo (create/edit); required ao habilitar. */
      idAreaFuncional: [{ value: null, disabled: true }],
      itemContratual: [null, Validators.required],
      observacao: [null],
      frequencias: this.fb.array([]),
    };
    if (this.idRelatorio) {
      // Caso de edição de relatório operacional
      this.form = this.fb.group({
        ...commonFields,
        prazo: [null, Validators.required],
        periodoReferenciaInicio: [null],
        periodoReferenciaFim: [null],
      });
    } else {
      // Caso de novo relatório operacional
      this.form = this.fb.group({
        ...commonFields,
        dataPrazo: [null],
        tipoDataBase: [null, Validators.required],
        tipoPrazo: [null, Validators.required],
        periodoReferenciaInicio: [null],
        periodoReferenciaFim: [null],
      });
    }
  }

  private loadData(): void {
    if (this.from === 'contratual') {
      this.getNomeRelatorio();
      this.getTipoRelatorio();
    } else {
      this.getTipoRelatorioVinculado();
    }
  }

  private observarTipoRelatorio(): void {
    this.form
      .get('idTipoRelatorio')
      ?.valueChanges.subscribe((valorSelecionado: any) => {
        const areaFuncionalControl = this.form.get('idAreaFuncional');
        const tipoSelecionado = this.tiposRelatorio.find(
          (tipo: any) => tipo.id === valorSelecionado,
        );
        const nomeTipo = tipoSelecionado?.nome?.toLowerCase?.() || '';

        if (nomeTipo.includes('monitora')) {
          areaFuncionalControl?.enable();
          areaFuncionalControl?.setValidators([Validators.required]);
          this.getAreasFuncionais(valorSelecionado);
        } else {
          areaFuncionalControl?.reset();
          areaFuncionalControl?.disable();
          areaFuncionalControl?.clearValidators();
          this.getAreasFuncionais();
        }

        areaFuncionalControl?.updateValueAndValidity();
      });
  }

  carregarRelatorio(id: number): void {
    if (this.from == 'contratual') {
      this.relatoriosContratuaisService
        .getRelatorioPorId(id, this.idContract)
        .pipe(take(1))
        .subscribe({
          next: (relatorio: any) => {
            this.isContratualEditManual = true;
            this.form.patchValue({
              idRelatorio: relatorio.relatorio.id,
              idTipoRelatorio: relatorio.tipoRelatorio.id,
              idAreaFuncional: relatorio.areaFuncional
                ? relatorio.areaFuncional.id
                : null,
              itemContratual: relatorio.itemContratual
                ? relatorio.itemContratual
                : null,
              prazo: relatorio.prazo ? this.parseDate(relatorio.prazo) : null,
              periodoReferenciaInicio: relatorio.periodoReferenciaInicio
                ? this.parseDate(relatorio.periodoReferenciaInicio)
                : null,
              periodoReferenciaFim: relatorio.periodoReferenciaFim
                ? this.parseDate(relatorio.periodoReferenciaFim)
                : null,
              observacao: relatorio.observacao ? relatorio.observacao : null,
            });

            if (relatorio.areaFuncional) {
              this.form.get('idAreaFuncional')?.enable();
              this.form
                .get('idAreaFuncional')
                ?.setValidators([Validators.required]);
              this.form.get('idAreaFuncional')?.updateValueAndValidity();
            }

            if (!this.isContratualEditManual) {
              this.form.get('periodoReferenciaInicio')?.disable();
              this.form.get('periodoReferenciaFim')?.disable();
            } else {
              this.form.get('periodoReferenciaInicio')?.enable();
              this.form.get('periodoReferenciaFim')?.enable();
            }
          },
          error: () => {
            this.toastService.showError('Erro ao carregar relatório.');
          },
        });
    } else {
      this.relatoriosOperacionaisService
        .getRelatorioPorId(id, this.idContract)
        .pipe(take(1))
        .subscribe({
          next: (relatorio: any) => {
            this.isOperacionalEditManual = true;
            this.form.patchValue({
              idRelatorio: relatorio.relatorioVinculado.id,
              idTipoRelatorio: relatorio.tipoRelatorioVinculado.id,
              idAreaFuncional: relatorio.areaFuncional?.id,
              itemContratual: relatorio.itemContratual
                ? relatorio.itemContratual
                : null,
              prazo: relatorio.prazo,
              dataPrazo: relatorio.dataPrazo,
              periodoReferenciaInicio: relatorio.periodoReferenciaInicio
                ? this.parseDate(relatorio.periodoReferenciaInicio)
                : null,
              periodoReferenciaFim: relatorio.periodoReferenciaFim
                ? this.parseDate(relatorio.periodoReferenciaFim)
                : null,
              observacao: relatorio.observacao,
            });

            if (!this.isOperacionalEditManual) {
              this.form.get('periodoReferenciaInicio')?.disable();
              this.form.get('periodoReferenciaFim')?.disable();
            } else {
              this.form.get('periodoReferenciaInicio')?.enable();
              this.form.get('periodoReferenciaFim')?.enable();
            }

            if (relatorio.areaFuncional) {
              this.areasFuncionais = [relatorio.areaFuncional];
            }
            if (relatorio.relatorioVinculado) {
              this.nomesRelatorios = [relatorio.relatorioVinculado];
            }

            if (relatorio.tipoRelatorioVinculado.id) {
              this.setOperacionalAreaEnabled();
              this.setOperacionalRelatorioEnabled();
              this.getTipoRelatorioVinculado(relatorio.relatorioVinculado.id, {
                preserveAreaId: relatorio.areaFuncional?.id,
              });
            }
          },
          error: () => {
            this.toastService.showError('Erro ao carregar relatório.');
          },
        });
    }
  }

  onPrazoChange(valor: string): void {
    if (this.from == 'contratual') {
      let tipoDataBase = this.form.get('tipoDataBase');
      let prazo = this.form.get('prazo');
      let periodoReferenciaInicio = this.form.get('periodoReferenciaInicio');
      let periodoReferenciaFim = this.form.get('periodoReferenciaFim');

      if (valor == 'Manual') {
        if (tipoDataBase) {
          tipoDataBase.reset();
          tipoDataBase.clearValidators();
          tipoDataBase.disable();
          tipoDataBase.updateValueAndValidity();
        }
        if (prazo) {
          prazo.setValidators(Validators.required);
          prazo.updateValueAndValidity();
        }
        if (periodoReferenciaInicio) {
          periodoReferenciaInicio.setValidators(Validators.required);
          periodoReferenciaInicio.updateValueAndValidity();
        }
        if (periodoReferenciaFim) {
          periodoReferenciaFim.setValidators(Validators.required);
          periodoReferenciaFim.updateValueAndValidity();
        }
      } else {
        if (tipoDataBase) {
          tipoDataBase.enable();
          if (valor) {
            tipoDataBase.setValidators(Validators.required);
          } else {
            tipoDataBase.clearValidators();
          }
          tipoDataBase.updateValueAndValidity();
        }
        if (prazo) {
          prazo.reset();
          prazo.clearValidators();
          prazo.updateValueAndValidity();
        }
        if (periodoReferenciaInicio) {
          periodoReferenciaInicio.reset();
          periodoReferenciaInicio.clearValidators();
          periodoReferenciaInicio.updateValueAndValidity();
        }
        if (periodoReferenciaFim) {
          periodoReferenciaFim.reset();
          periodoReferenciaFim.clearValidators();
          periodoReferenciaFim.updateValueAndValidity();
        }
      }

      if (valor === 'Personalizado' && this.frequencias.length === 0) {
        this.adicionarLinhaFrequencia();
      } else if (valor !== 'Personalizado') {
        this.frequencias.clear();
      }
      return;
    }

    if (valor === 'Personalizado' && this.frequencias.length === 0) {
      this.adicionarLinhaFrequencia();
    } else if (valor !== 'Personalizado') {
      let tipoDataBase = this.form.get('tipoDataBase');
      let dataPrazo = this.form.get('dataPrazo');
      let periodoReferenciaInicio = this.form.get('periodoReferenciaInicio');
      let periodoReferenciaFim = this.form.get('periodoReferenciaFim');

      if (valor == 'Manual') {
        if (dataPrazo) {
          dataPrazo.setValidators(Validators.required);
          dataPrazo.updateValueAndValidity();
        }
        if (tipoDataBase) {
          tipoDataBase.reset();
          tipoDataBase.clearValidators();
          tipoDataBase.disable();
          tipoDataBase.updateValueAndValidity();
        }
        if (periodoReferenciaInicio) {
          periodoReferenciaInicio.setValidators(Validators.required);
          periodoReferenciaInicio.updateValueAndValidity();
        }
        if (periodoReferenciaFim) {
          periodoReferenciaFim.setValidators(Validators.required);
          periodoReferenciaFim.updateValueAndValidity();
        }
      } else {
        if (dataPrazo) {
          dataPrazo.reset();
          dataPrazo.clearValidators();
          dataPrazo.updateValueAndValidity();
        }
        if (tipoDataBase) {
          tipoDataBase.enable();
          tipoDataBase.setValidators(Validators.required);
          tipoDataBase.updateValueAndValidity();
        }
        if (periodoReferenciaInicio) {
          periodoReferenciaInicio.reset();
          periodoReferenciaInicio.clearValidators();
          periodoReferenciaInicio.updateValueAndValidity();
        }
        if (periodoReferenciaFim) {
          periodoReferenciaFim.reset();
          periodoReferenciaFim.clearValidators();
          periodoReferenciaFim.updateValueAndValidity();
        }
      }
      this.frequencias.clear();
    }
  }

  adicionarLinhaFrequencia(): void {
    this.frequencias.push(
      this.fb.group({
        frequencia: [null, Validators.required],
        periodo: [null, Validators.required],
      }),
    );
  }

  removerLinhaFrequencia(index: number): void {
    this.frequencias.removeAt(index);
  }

  cadastrar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastService.showError(
        'Preencha todos os campos obrigatórios corretamente.',
      );
      return;
    }

    const raw = this.form.getRawValue();

    if (
      this.from == 'contratual' &&
      !this.idRelatorio &&
      raw.tipoPrazo == 'Manual'
    ) {
      if (
        !raw.prazo ||
        !raw.periodoReferenciaInicio ||
        !raw.periodoReferenciaFim
      ) {
        this.toastService.showError(
          'Preencha todos os campos obrigatórios corretamente.',
        );
        return;
      }
    }

    if (
      this.from == 'contratual' &&
      (!this.idRelatorio || this.isContratualEditManual)
    ) {
      if (
        (raw.periodoReferenciaInicio && !raw.periodoReferenciaFim) ||
        (!raw.periodoReferenciaInicio && raw.periodoReferenciaFim)
      ) {
        this.toastService.showError(
          'Preencha o período de referência completo.',
        );
        return;
      }

      if (raw.periodoReferenciaInicio && raw.periodoReferenciaFim) {
        if (
          moment(raw.periodoReferenciaInicio).isAfter(
            moment(raw.periodoReferenciaFim),
            'day',
          )
        ) {
          this.toastService.showError(
            'O período de referência inicial não pode ser maior que o final.',
          );
          return;
        }
      }
    }

    if (
      this.from == 'operacional' &&
      (!this.idRelatorio || this.isOperacionalEditManual)
    ) {
      if (
        (raw.periodoReferenciaInicio && !raw.periodoReferenciaFim) ||
        (!raw.periodoReferenciaInicio && raw.periodoReferenciaFim)
      ) {
        this.toastService.showError(
          'Preencha o período de referência completo.',
        );
        return;
      }

      if (raw.periodoReferenciaInicio && raw.periodoReferenciaFim) {
        if (
          moment(raw.periodoReferenciaInicio).isAfter(
            moment(raw.periodoReferenciaFim),
            'day',
          )
        ) {
          this.toastService.showError(
            'O período de referência inicial não pode ser maior que o final.',
          );
          return;
        }
      }
    }

    let dados: any;
    // Transformar os dados para o backend
    if (this.from == 'contratual') {
      if (!this.idRelatorio) {
        dados = {
          idRelatorio: raw.idRelatorio,
          idTipoRelatorio: raw.idTipoRelatorio,
          idAreaFuncional: raw.idAreaFuncional,
          itemContratual: raw.itemContratual,
          tipoPrazo: raw.tipoPrazo,
          observacao: raw.observacao,
          periodosPersonalizados: [],
        };
        if (raw.tipoPrazo == 'Manual') {
          dados.prazo = moment(raw.prazo).format('YYYY-MM-DD');
          dados.periodoReferenciaInicio = moment(
            raw.periodoReferenciaInicio,
          ).format('YYYY-MM-DD');
          dados.periodoReferenciaFim = moment(raw.periodoReferenciaFim).format(
            'YYYY-MM-DD',
          );
        } else {
          dados.tipoDataBase = raw.tipoDataBase;
        }
      } else {
        dados = {
          idRelatorio: raw.idRelatorio,
          idTipoRelatorio: raw.idTipoRelatorio,
          idAreaFuncional: raw.idAreaFuncional,
          itemContratual: raw.itemContratual,
          observacao: raw.observacao,
          prazo: moment(raw.prazo).format('YYYY-MM-DD'),
        };
        if (
          this.isContratualEditManual &&
          raw.periodoReferenciaInicio &&
          raw.periodoReferenciaFim
        ) {
          dados.periodoReferenciaInicio = moment(
            raw.periodoReferenciaInicio,
          ).format('YYYY-MM-DD');
          dados.periodoReferenciaFim = moment(raw.periodoReferenciaFim).format(
            'YYYY-MM-DD',
          );
        }
      }
    } else {
      if (!this.idRelatorio) {
        if (raw.tipoPrazo == 'Manual') {
          if (
            !raw.dataPrazo ||
            !raw.periodoReferenciaInicio ||
            !raw.periodoReferenciaFim
          ) {
            this.toastService.showError(
              'Preencha todos os campos obrigatórios corretamente.',
            );
            return;
          }
        }
        dados = {
          idRelatorio: raw.idRelatorio,
          idTipoRelatorio: raw.idTipoRelatorio,
          idAreaFuncional: raw.idAreaFuncional,
          tipoPrazo: raw.tipoPrazo,
          dataPrazo: raw.dataPrazo
            ? moment(raw.dataPrazo).format('YYYY-MM-DD')
            : '',
          itemContratual: raw.itemContratual,
          observacao: raw.observacao,
          periodosPersonalizados: [],
        };
        if (raw.tipoPrazo != 'Manual') {
          dados.tipoDataBase = raw.tipoDataBase;
        } else {
          dados.periodoReferenciaInicio = moment(
            raw.periodoReferenciaInicio,
          ).format('YYYY-MM-DD');
          dados.periodoReferenciaFim = moment(raw.periodoReferenciaFim).format(
            'YYYY-MM-DD',
          );
        }
      } else {
        dados = {
          idRelatorio: raw.idRelatorio,
          idTipoRelatorio: raw.idTipoRelatorio,
          idAreaFuncional: raw.idAreaFuncional,
          itemContratual: raw.itemContratual,
          dataPrazo: raw.dataPrazo
            ? moment(raw.dataPrazo).format('YYYY-MM-DD')
            : '',
          tipoPrazo: raw.tipoPrazo,
          observacao: raw.observacao,
          prazo: moment(raw.prazo).format('YYYY-MM-DD'),
          tipoDataBase: raw.tipoDataBase,
        };
        if (
          this.isOperacionalEditManual &&
          raw.periodoReferenciaInicio &&
          raw.periodoReferenciaFim
        ) {
          dados.periodoReferenciaInicio = moment(
            raw.periodoReferenciaInicio,
          ).format('YYYY-MM-DD');
          dados.periodoReferenciaFim = moment(raw.periodoReferenciaFim).format(
            'YYYY-MM-DD',
          );
        }
      }
    }

    if (raw.tipoPrazo === 'Personalizado' && !this.idRelatorio) {
      dados.periodosPersonalizados = raw.frequencias.map((freq: any) => {
        const [inicio, fim] = freq.periodo
          .split(' - ')
          .map((v: string) => parseInt(v, 10));
        return {
          frequencia: freq.frequencia,
          periodoInicial: inicio,
          periodoFinal: fim,
        };
      });
    }

    if (this.from == 'contratual') {
      const request$ = this.data?.idRelatorio
        ? this.relatoriosContratuaisService.atualizarRelatorio(
            this.data.idRelatorio,
            this.idContract,
            dados,
          )
        : this.relatoriosContratuaisService.criarRelatorio(
            dados,
            this.idContract,
          );

      request$.pipe(take(1)).subscribe({
        next: () => {
          const msg = this.data?.idRelatorio
            ? 'Relatório atualizado com sucesso.'
            : 'Relatório cadastrado com sucesso.';
          this.toastService.showSuccess(msg);
          this.dialogRef.close(true);
        },
        error: (error) => {
          const msg = this.data?.idRelatorio
            ? error.error.message
            : error.error.message;
          this.toastService.showError(msg);
        },
      });
    } else {
      const request$ = this.data?.idRelatorio
        ? this.relatoriosOperacionaisService.atualizarRelatorio(
            this.data.idRelatorio,
            this.idContract,
            dados,
          )
        : this.relatoriosOperacionaisService.criarRelatorio(
            dados,
            this.idContract,
          );

      request$.pipe(take(1)).subscribe({
        next: () => {
          const msg = this.data?.idRelatorio
            ? 'Relatório atualizado com sucesso.'
            : 'Relatório cadastrado com sucesso.';
          this.toastService.showSuccess(msg);
          this.dialogRef.close(true);
        },
        error: (error) => {
          const msg = this.data?.idRelatorio
            ? error.error.message
            : error.error.message;
          this.toastService.showError(msg);
        },
      });
    }
  }

  cancelar(): void {
    this.dialogRef.close();
  }

  onPeriodoInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, ''); // remove tudo que não for dígito

    if (value.length > 4) {
      value = value.slice(0, 4);
    }

    // Formata como "NN - NN"
    if (value.length >= 3) {
      value = `${value.slice(0, 2)} - ${value.slice(2, 4)}`;
    } else if (value.length >= 1) {
      value = value.slice(0, 2);
    }

    input.value = value;
  }

  // === OBTÉM TIPOS DE PARECER ===
  getNomeRelatorio(): void {
    this.relatoriosContratuaisService
      .getNomeRelatorio()
      .pipe(take(1))
      .subscribe({
        next: (results: any) => {
          this.nomesRelatorios = results?.content ?? [];
        },
        error: (erro: any) => {
          this.toastService.showError('Erro ao buscar tipos de parecer.');
        },
      });
  }

  private setOperacionalAreaEnabled(): void {
    if (this.from !== 'operacional') {
      return;
    }
    const c = this.form.get('idAreaFuncional');
    c?.enable({ emitEvent: false });
    c?.setValidators([Validators.required]);
    c?.updateValueAndValidity({ emitEvent: false });
  }

  private setOperacionalAreaDisabled(): void {
    if (this.from !== 'operacional') {
      return;
    }
    const c = this.form.get('idAreaFuncional');
    if (!c) {
      return;
    }
    c.clearValidators();
    c.reset(null, { emitEvent: false });
    c.disable({ emitEvent: false });
    c.updateValueAndValidity({ emitEvent: false });
  }

  private setOperacionalRelatorioEnabled(): void {
    if (this.from !== 'operacional') {
      return;
    }
    const c = this.form.get('idRelatorio');
    c?.enable({ emitEvent: false });
    c?.setValidators([Validators.required]);
    c?.updateValueAndValidity({ emitEvent: false });
  }

  private setOperacionalRelatorioDisabled(): void {
    if (this.from !== 'operacional') {
      return;
    }
    const c = this.form.get('idRelatorio');
    if (!c) {
      return;
    }
    c.clearValidators();
    c.reset(null, { emitEvent: false });
    c.disable({ emitEvent: false });
    c.updateValueAndValidity({ emitEvent: false });
  }

  /** Tipo de relatório operacional → limpa área e nome e recarrega opções filtradas. */
  onOperacionalTipoRelatorioChange(): void {
    if (this.from !== 'operacional') {
      return;
    }
    this.form.patchValue(
      { idAreaFuncional: null, idRelatorio: null },
      { emitEvent: false },
    );
    const idTipo = this.form.get('idTipoRelatorio')?.value;

    if (!idTipo) {
      this.setOperacionalAreaDisabled();
      this.setOperacionalRelatorioDisabled();
      this.areasFuncionais = [];
      this.nomesRelatorios = [];
      return;
    }
    this.setOperacionalAreaEnabled();
    this.setOperacionalRelatorioEnabled();
    this.getNomeRelatorioVinculado(
      this.idRelatorio ? 'selectionChange' : undefined,
    );
  }

  // === OBTÉM TIPOS DE PARECER ===
  getNomeRelatorioVinculado(
    data?: any,
    opts?: { preserveAreaId?: string },
  ): void {
    const idTipoRelatorio = this.form.controls['idTipoRelatorio'].value;
    const tipo = this.tiposRelatorio.find(
      (rel: any) => rel.id == idTipoRelatorio,
    );
    const codigo = tipo?.codigo ?? '';

    if (!idTipoRelatorio) {
      this.areasFuncionais = [];
      this.nomesRelatorios = [];
      if (this.from === 'operacional') {
        this.setOperacionalAreaDisabled();
        this.setOperacionalRelatorioDisabled();
      }
      return;
    }

    if (this.from === 'operacional') {
      this.setOperacionalAreaEnabled();
      this.setOperacionalRelatorioEnabled();
    }

    const seq = ++this.operacionalCargaSeq;
    const idAreaPreferida =
      opts != null && 'preserveAreaId' in opts
        ? opts.preserveAreaId
        : this.form.get('idAreaFuncional')?.value;

    this.getAreasFuncionaisOperacional(
      idTipoRelatorio,
      idAreaPreferida,
      () => {
        if (seq !== this.operacionalCargaSeq) {
          return;
        }
        this.relatoriosOperacionaisService
          .getNomeRelatorio(codigo)
          .pipe(take(1))
          .subscribe({
            next: (results: any) => {
              if (seq !== this.operacionalCargaSeq) {
                return;
              }
              this.nomesRelatorios = results?.content ?? [];
              if (
                this.idRelatorio &&
                data !== undefined &&
                data !== 'selectionChange'
              ) {
                this.form.patchValue({ idRelatorio: data });
              }
              if (data === 'selectionChange') {
                this.form.patchValue({ idRelatorio: null });
              }
            },
            error: (erro: any) => {
              const msg =
                erro?.error?.message ?? 'Erro ao buscar nomes de relatório.';
              this.toastService.showError(msg);
            },
          });
      },
      seq,
    );
  }

  // === OBTÉM TIPOS DE PARECER ===
  getTipoRelatorioVinculado(
    relatorioVinculadoId?: string,
    opts?: { preserveAreaId?: string },
  ): void {
    this.relatoriosOperacionaisService
      .getTipoRelatorio()
      .pipe(take(1))
      .subscribe({
        next: (results: any) => {
          this.tiposRelatorio = results?.content ?? [];
          if (this.idRelatorio && relatorioVinculadoId !== undefined) {
            this.getNomeRelatorioVinculado(relatorioVinculadoId, {
              preserveAreaId: opts?.preserveAreaId,
            });
          }
        },
        error: (erro: any) => {
          const msg =
            erro?.error?.message ?? 'Erro ao buscar tipos de relatório.';
          this.toastService.showError(msg);
        },
      });
  }

  // === OBTÉM TIPOS DE PARECER ===
  getTipoRelatorio(): void {
    this.relatoriosContratuaisService
      .getTipoRelatorio()
      .pipe(take(1))
      .subscribe({
        next: (results: any) => {
          this.tiposRelatorio = results?.content ?? [];
          const idTipoRelatorio = this.form.get('idTipoRelatorio')?.value;

          if (idTipoRelatorio) {
            const tipoSelecionado = this.tiposRelatorio.find(
              (tipo: any) => tipo.id === idTipoRelatorio,
            );
            const nomeTipo = tipoSelecionado?.nome?.toLowerCase?.() || '';

            if (nomeTipo.includes('monitora')) {
              this.getAreasFuncionais(idTipoRelatorio);
            }
          }
        },
        error: (erro: any) => {
          this.toastService.showError('Erro ao buscar tipos de parecer.');
        },
      });
  }

  // === OBTÉM TIPOS DE PARECER ===
  getAreasFuncionais(idTipoRelatorio?: any): void {
    this.relatoriosContratuaisService
      .getAreasFuncionais(idTipoRelatorio)
      .pipe(take(1))
      .subscribe({
        next: (results: any) => {
          this.areasFuncionais = results?.content ?? [];
          if (idTipoRelatorio && this.form.get('idAreaFuncional')?.value) {
            const areaSelecionada = this.areasFuncionais.find(
              (area: any) => area.id == this.form.get('idAreaFuncional')?.value,
            );

            if (!areaSelecionada) {
              this.form.get('idAreaFuncional')?.reset();
            }
          }
        },
        error: (erro: any) => {
          this.toastService.showError('Erro ao buscar tipos de parecer.');
        },
      });
  }

  private getAreasFuncionaisOperacional(
    idTipoRelatorio: any,
    idAreaPreferida: any,
    onAfter: () => void,
    cargaSeq: number,
  ): void {
    if (!idTipoRelatorio) {
      this.areasFuncionais = [];
      onAfter();
      return;
    }

    this.relatoriosOperacionaisService
      .getAreasFuncionais(idTipoRelatorio)
      .pipe(take(1))
      .subscribe({
        next: (results: any) => {
          if (cargaSeq !== this.operacionalCargaSeq) {
            return;
          }
          this.areasFuncionais = results?.content ?? [];
          const valorAlvo =
            idAreaPreferida !== undefined && idAreaPreferida !== null
              ? idAreaPreferida
              : this.form.get('idAreaFuncional')?.value;

          if (valorAlvo) {
            const areaOk = this.areasFuncionais.some(
              (a: any) => a.id == valorAlvo,
            );
            if (areaOk) {
              this.form.patchValue(
                { idAreaFuncional: valorAlvo },
                { emitEvent: false },
              );
            } else {
              this.form
                .get('idAreaFuncional')
                ?.reset(null, { emitEvent: false });
            }
          }
          onAfter();
        },
        error: (erro: any) => {
          if (cargaSeq !== this.operacionalCargaSeq) {
            return;
          }
          this.areasFuncionais = [];
          const msg =
            erro?.error?.message ?? 'Erro ao buscar áreas funcionais.';
          this.toastService.showError(msg);
          onAfter();
        },
      });
  }

  formatarDataPrazo(event: Event): void {
    const input = event.target as HTMLInputElement;
    let valor = input.value;
    const formControl = this.form.get('dataPrazo');

    if (formControl) {
      // 1. Remove qualquer caractere que não seja dígito
      valor = valor.replace(/\D/g, '');
      // 2. Limita o valor a 6 dígitos (mmAaaa)
      if (valor.length > 6) {
        valor = valor.slice(0, 6);
      }

      // 3. Aplica a máscara 'mm/AAAA'
      if (valor.length > 2) {
        valor = `${valor.slice(0, 2)}/${valor.slice(2, 6)}`;
      }

      // 4. Atualiza o valor do FormControl
      formControl.setValue(valor, { emitEvent: false });
    }
  }

  getModalTitle(): string {
    if (this.from == 'contratual') {
      return this.idRelatorio
        ? 'Editar Relatório de Monitoração'
        : 'Novo Relatório de Monitoração';
    }
    return this.idRelatorio
      ? 'Editar Relatório Operacional'
      : 'Novo Relatório Operacional';
  }

  getPrazos(): string[] {
    return this.prazos;
  }

  isContratualCreate(): boolean {
    return this.from == 'contratual' && !this.idRelatorio;
  }

  showContratualDataBase(): boolean {
    return this.isContratualCreate();
  }

  showContratualManualFields(): boolean {
    if (this.from != 'contratual') {
      return false;
    }
    if (this.idRelatorio) {
      return true;
    }
    return this.form.get('tipoPrazo')?.value == 'Manual';
  }

  showContratualPeriodicType(): boolean {
    return (
      this.isContratualCreate() &&
      this.form.get('tipoPrazo')?.value == 'Personalizado'
    );
  }

  private parseDate(data: string): Date | null {
    if (!data) {
      return null;
    }
    return moment(data, 'YYYY-MM-DD').toDate();
  }
}
